import { usePracticeSearch } from "./use-practice-search";
import {
  portfolioRows,
  episodeRows,
  practiceSearchRows,
} from "./account-search-rows";
import type { Schemas } from "@softmax/observatory-client/types";
import { buildHref } from "@softmax/web-addresses";

import { policySearchQueryOptions } from "@/features/search/records";
import { leaguePortfolioQueryOptions } from "@softmax/web-features/site/queries";
import {
  type SearchInputs,
  maxResultsPerGroup,
  type SearchState,
  type PillSearchGroup,
  pillSearchGroup,
} from "./pill-search-records";
import { useSearchQuery } from "./use-search-query";
import { championEpisodesQueryOptions } from "./pill-search.queries";
export function useAccountSearch({ query, enabled, ready }: SearchInputs) {
  const versions = useSearchQuery({
    ...policySearchQueryOptions({
      query,
      limit: maxResultsPerGroup,
      mine: true,
    }),
    enabled: enabled && ready,
  });
  const portfolio = useSearchQuery({
    ...leaguePortfolioQueryOptions(),
    enabled,
  });
  const practice = usePracticeSearch({ enabled });
  const seats = portfolio.data ?? [];
  const champions = [
    ...new Set(
      seats
        .filter((seat) => seat.is_champion)
        .map((seat) => seat.policy_version_id),
    ),
  ].slice(0, 2);
  const firstEpisodes = useSearchQuery({
    ...championEpisodesQueryOptions(champions[0] ?? ""),
    enabled: enabled && champions.length > 0,
  });
  const secondEpisodes = useSearchQuery({
    ...championEpisodesQueryOptions(champions[1] ?? ""),
    enabled: enabled && champions.length > 1,
  });
  const episodeQueries: SearchState[] = [portfolio];
  const episodes: Schemas["EpisodePage"]["entries"] = [];

  if (champions.length > 0) {
    episodeQueries.push(firstEpisodes);
    episodes.push(...(firstEpisodes.data?.entries ?? []));
  }

  if (champions.length > 1) {
    episodeQueries.push(secondEpisodes);
    episodes.push(...(secondEpisodes.data?.entries ?? []));
  }

  const groups: PillSearchGroup[] = [];

  if (ready)
    groups.push(
      pillSearchGroup({
        heading: "Versions",
        rows: (versions.data ?? []).map((row) => ({
          key: row.policy_version_id,
          href: buildHref({
            name: "policyVersion",
            policyVersionId: row.policy_version_id,
          }),
          label: `${row.policy_name}:v${row.version}`,
          secondary: row.player_name ?? undefined,
        })),
        queries: [versions],
      }),
    );

  groups.push(
    pillSearchGroup({
      heading: "Your leagues",
      rows: portfolioRows({
        seats,
        query,
      }),
      queries: [portfolio],
    }),
  );
  groups.push(
    pillSearchGroup({
      heading: "Practice runs",
      rows: practiceSearchRows({ rows: practice.rows, query }),
      queries: [practice.state],
    }),
  );
  groups.push(
    pillSearchGroup({
      heading: "Episodes",
      rows: episodeRows({
        episodes,
        seats,
        query,
      }),
      queries: episodeQueries,
    }),
  );
  return groups;
}
